import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {
  AuthorizationDownloadXmlRequestDTO,
  QuotaFcpSettingsRequest,
} from './Settings.types';
import {
  createAuthorizationDownloadXml,
  createInvoiceSettings,
  createQuotaFcpSettings,
  deleteAuthorizationDownloadXml,
  getAuthorizationDownloadXmlId,
  getInvoiceSettingsId,
  listAuthorizationDownloadXml,
  listQuotaFcpSettings,
  updateAuthorizationDownloadXml,
  updateInvoiceSettings,
  updateQuotaFcpSettings,
} from './Settings.service';
import { useServiceError } from '@/hooks';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { SettingsErrors } from './Settings.errors';
import { AxiosError } from 'axios';

export function useCreateInvoiceSettings() {
  const queryClient = useQueryClient();

  return useMutation(createInvoiceSettings, {
    async onSuccess(_, data) {
      notification.success({
        message: 'Configuração de nota',
        description: 'Os dados de configuração da NF foram salvos.',
      });
      await queryClient.invalidateQueries([
        'invoiceSettings/company',
        data.companyId,
      ]);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => SettingsErrors[err] || err),
      });
    },
  });
}

export function useUpdateInvoiceSettings() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  return useMutation(updateInvoiceSettings, {
    async onSuccess(_, data) {
      notification.success({
        message: 'Configuração de nota',
        description: intl({
          id: 'companies.create.notifications.success.description',
        }),
      });
      await queryClient.invalidateQueries([
        'invoiceSettings/company',
        data.companyId,
      ]);
    },

    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => SettingsErrors[err] || err),
      });
    },
  });
}

export function useInvoiceSettingsByCompany(
  companyId?: number,
  enabled = true,
) {
  const mutation = useQuery(
    ['invoiceSettings/company', companyId],
    () => getInvoiceSettingsId(companyId),
    {
      onError(error: AxiosError) {
        if (
          error.response?.data.message?.includes(
            'Invoice settings not found for company',
          )
        ) {
          return notification.info({
            message: error.response?.data.message.replace(
              error.response?.data.message,
              'Esta empresa não possui configuração de nota fiscal.',
            ),
          });
        }

        notificationError(error);
      },
      enabled: enabled && !!companyId,
    },
  );

  return mutation;
}

// AuthorizationDownloadXml

export function useAuthorizationDownloadXml(id?: number) {
  const query = useQuery(
    ['authorizationDownloadXml', id],
    () => listAuthorizationDownloadXml(id),
    {
      enabled: !!id,
    },
  );

  useServiceError(query.error);
  return query;
}

export function useCreateAuthorizationDownloadXml() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: AuthorizationDownloadXmlRequestDTO) =>
      createAuthorizationDownloadXml(obj),
    {
      onSuccess() {
        notification.success({
          message: intl({
            id: 'companies.document.xml.notifications.success.title',
          }),
          description: intl({
            id: 'companies.create.document.xml.notifications.success.description',
          }),
        });

        queryClient.invalidateQueries(['authorizationDownloadXml']);
      },
    },
  );
  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteAuthorizationDownloadXml(invoiceSettingsId?: number) {
  const intl = useIntl();

  const queryClient = useQueryClient();

  const mutation = useMutation(
    (id: number) => deleteAuthorizationDownloadXml(id),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'companies.document.xml.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'companies.delete.document.xml.notifications.success.description',
          }),
        });

        if (invoiceSettingsId) {
          await queryClient.invalidateQueries([
            'authorizationDownloadXml',
            invoiceSettingsId,
          ]);
        }
      },
    },
  );
  useServiceError(mutation.error);

  return mutation;
}

export function useAuthorizationDownloadXmlId() {
  const mutation = useMutation((companyId: number) =>
    getAuthorizationDownloadXmlId(companyId),
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateAuthorizationDownloadXml() {
  const intl = useIntl().formatMessage;
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({ obj, id }: { obj: AuthorizationDownloadXmlRequestDTO; id: number }) =>
      updateAuthorizationDownloadXml(obj, id),
    {
      onSuccess() {
        notification.success({
          message: intl({
            id: 'companies.document.xml.notifications.success.title',
          }),
          description: intl({
            id: 'invoice.create.notification.successDescription.edit',
          }),
        });

        queryClient.invalidateQueries(['authorizationDownloadXml']);
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}

// Alíquota

export function useQuotaFcpSettings(id: number) {
  const query = useInfiniteQuery(['quotaFcpSettings'], () =>
    listQuotaFcpSettings(id),
  );

  useServiceError(query.error);

  return query;
}

export function useCreateQuotaFcpSettings() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({ obj, id }: { obj: QuotaFcpSettingsRequest; id: number }) =>
      createQuotaFcpSettings({ obj, id }),
    {
      onSuccess() {
        notification.success({
          message: 'Sucesso!',
          description: 'Alíquota criada',
        });

        queryClient.invalidateQueries(['quotaFcpSettings']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useEditeQuotaFcpSettings() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    ({ obj, id }: { obj: QuotaFcpSettingsRequest; id: number }) =>
      updateQuotaFcpSettings({ obj, id }),
    {
      onSuccess() {
        notification.success({
          message: 'Sucesso!',
          description: 'Alíquota editada',
        });

        queryClient.invalidateQueries(['quotaFcpSettings']);
      },
    },
  );
  useServiceError(mutation.error);
  return mutation;
}
